import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useState } from 'react';
import { useDepositCheck, type CheckTransaction } from '@/hooks/queries/useChecks';
import { toast } from '@/components/ui/toast/useToast';

interface DepositCheckModalProps {
  open: boolean;
  onClose: () => void;
  check: CheckTransaction | null;
}

export function DepositCheckModal({ open, onClose, check }: DepositCheckModalProps) {
  const [depositedDate, setDepositedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const depositCheck = useDepositCheck();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!check) return;

    setIsSubmitting(true);

    try {
      await depositCheck.mutateAsync({
        checkId: check.id,
        depositedDate,
        notes: notes || undefined,
      });

      toast.success(`Le chèque ${check.check_number} a été marqué comme déposé.`);

      setNotes('');
      onClose();
    } catch (error) {
      console.error('Error depositing check:', error);
      toast.error('Impossible de déposer le chèque. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!check) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>🏦 Déposer le chèque</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">N° Chèque:</span>
              <span className="font-mono font-semibold">{check.check_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Tireur:</span>
              <span className="font-medium">{check.check_drawer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Montant:</span>
              <span className="text-lg font-bold text-green-600">
                {formatCurrency(check.amount)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="depositedDate">Date de dépôt *</Label>
            <Input
              id="depositedDate"
              type="date"
              value={depositedDate}
              onChange={(e) => setDepositedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optionnel)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Déposé à l'agence Tlemcen Centre..."
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Dépôt en cours...' : '🏦 Déposer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
